// Wishlist/favorite boutiques slice. Guests keep an instant local list keyed by
// a stable clientId; signed-in buyers can sync/import that list into the
// server-owned buyer wishlist without breaking guest browsing.
import { api } from "@/api";
import { getBuyerAuthToken } from "@/api/token";
import type { LikeState } from "@/api/types";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { safeLocalStorage } from "./safe-storage";

const newClientId = () => `cw_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;

type WishlistState = {
	clientId: string;
	/** Когда витрину на этом устройстве открывали в последний раз (мс). По нему
	 *  гостевой кэш устройства сбрасывается через срок давности (store/device.ts). */
	lastSeenAt: number;
	ids: string[];
	shopIds: string[];
	ideaIds: string[];
	/** Выбранный размер по избранному товару (productId → size). Пишется, когда
	 *  на карточке товара выбран размер и нажато сердечко; страница избранного
	 *  подставляет его в селект. Сервер — источник истины (LikeState.productSizes). */
	sizes: Record<string, string>;
	has: (id: string) => boolean;
	hasShop: (id: string) => boolean;
	hasIdea: (id: string) => boolean;
	savedSize: (id: string) => string | undefined;
	toggleProduct: (id: string, size?: string) => boolean;
	toggleShop: (id: string) => boolean;
	toggleIdea: (id: string) => boolean;
	setServerState: (state: LikeState) => void;
	replaceServerState: (state: LikeState) => void;
	/** Оставить в избранном только реально существующие товары (по резолву byIds).
	 *  Чинит рассинхрон бейджа: сервер хранит лайк даже на снятый с публикации
	 *  товар, а страница избранного его не рисует. */
	pruneProducts: (validIds: string[]) => void;
	syncFromServer: () => Promise<LikeState>;
	setProductLikedOnServer: (productId: string, liked: boolean, size?: string) => Promise<void>;
	setShopLikedOnServer: (shopId: string, liked: boolean) => Promise<void>;
	setIdeaLikedOnServer: (ideaId: string, liked: boolean) => Promise<void>;
	clear: () => void;
	/** Новый гость на устройстве: новый clientId и пустое избранное. */
	resetDevice: () => void;
	touch: () => void;
	/** Применить серверное избранное, если с `mark` не было записей лайков
	 *  (см. `likeWriteMark`): иначе ответ устарел и снял бы свежий лайк. */
	applyServerStateSince: (mark: number, state: LikeState) => void;
};

const uniq = (values: string[]) => [...new Set(values)];

/**
 * Под каким именем идея лежит в избранном: `slug || id` карточки ленты.
 *
 * Шлюз отдаёт лайки идей именно этой ссылкой (engagement.ideaLikeRefs), и
 * мобилка лайкает ею же. Витрина лайкала и сверяла по `id` (`col_<slug>`,
 * `dcol_<id>`), а сверка с сервером заменяла список слагами: сердце гасло
 * после обновления, а «Избранное → Идеи» оставалось пустым (A-27, 02.10.2026).
 * Писать можно любой из двух форм — шлюз сам приводит к каноническому id.
 */
export const ideaLikeId = (idea: { id: string; slug?: string | null }): string => idea.slug || idea.id;
const hasBuyerToken = () => Boolean(getBuyerAuthToken());

/** Что сообщить покупателю, если лайк не записался на сервер. Витрина
 *  подставляет сюда тост; в тестах — перехватчик. */
let reportLikeError: (error: unknown) => void = () => {};
export const setWishlistErrorReporter = (fn: (error: unknown) => void): void => {
	reportLikeError = fn;
};

// Счётчики записей лайков. Сверка с сервером (syncFromServer) ЗАМЕНЯЕТ локальный
// список на серверный — и если покупатель успел нажать сердечко, пока шёл
// запрос, ответ сервера приходил «из прошлого» и молча снимал только что
// поставленный лайк. Поэтому сверка, во время которой была запись, свой
// результат не применяет.
let likeWrites = 0;
let likeWritesInFlight = 0;

type LikeList = "ids" | "shopIds" | "ideaIds";

/** Отметка «сколько записей лайков было к этому моменту» для applyServerStateSince. */
export const likeWriteMark = (): number => likeWrites;

/**
 * Записать лайк на сервер; если запись не прошла — вернуть сердечко как было и
 * сказать об этом покупателю. Раньше ошибку глотал `void`: сердечко горело,
 * а при следующей сверке с сервером лайк исчезал, и выглядело это как «лайки
 * не работают».
 */
async function writeLike(
	list: LikeList,
	id: string,
	liked: boolean,
	write: () => Promise<unknown>,
	rollback: (state: WishlistState) => Partial<WishlistState>,
	set: (fn: (s: WishlistState) => Partial<WishlistState>) => void,
	get: () => WishlistState,
): Promise<void> {
	likeWrites += 1;
	likeWritesInFlight += 1;
	try {
		await write();
	} catch (error) {
		// Откатываем, только если сердечко всё ещё в том положении, которое мы
		// пытались записать: покупатель мог уже нажать его повторно.
		if (get()[list].includes(id) === liked) set(rollback);
		reportLikeError(error);
	} finally {
		likeWritesInFlight -= 1;
	}
}

export const useWishlist = create<WishlistState>()(
	persist(
		(set, get) => ({
			clientId: newClientId(),
			lastSeenAt: Date.now(),
			ids: [],
			shopIds: [],
			ideaIds: [],
			sizes: {},
			has: (id) => get().ids.includes(id),
			hasShop: (id) => get().shopIds.includes(id),
			hasIdea: (id) => get().ideaIds.includes(id),
			savedSize: (id) => get().sizes[id],
			toggleProduct: (id, size) => {
				const liked = !get().ids.includes(id);
				set((s) => {
					const nextSizes = { ...s.sizes };
					if (liked) {
						if (size) nextSizes[id] = size;
					} else {
						delete nextSizes[id];
					}
					return {
						ids: liked ? [...s.ids, id] : s.ids.filter((x) => x !== id),
						sizes: nextSizes,
					};
				});
				return liked;
			},
			toggleShop: (id) => {
				const liked = !get().shopIds.includes(id);
				set((s) => ({
					shopIds: liked ? [...s.shopIds, id] : s.shopIds.filter((x) => x !== id),
				}));
				return liked;
			},
			toggleIdea: (id) => {
				const liked = !get().ideaIds.includes(id);
				set((s) => ({
					ideaIds: liked ? [...s.ideaIds, id] : s.ideaIds.filter((x) => x !== id),
				}));
				return liked;
			},
			setServerState: ({ productIds, shopIds, ideaIds, productSizes }) =>
				set((s) => ({
					ids: uniq([...s.ids, ...productIds]),
					shopIds: uniq([...s.shopIds, ...shopIds]),
					ideaIds: uniq([...s.ideaIds, ...ideaIds]),
					sizes: { ...s.sizes, ...(productSizes ?? {}) },
				})),
			replaceServerState: ({ productIds, shopIds, ideaIds, productSizes }) =>
				set((s) => ({
					ids: productIds,
					shopIds,
					ideaIds,
					// Сервер отдаёт размеры не всегда (старый бэк/гость) — тогда
					// сохраняем локально выбранные размеры для ещё избранных товаров.
					sizes:
						productSizes ??
						Object.fromEntries(Object.entries(s.sizes).filter(([id]) => productIds.includes(id))),
				})),
			pruneProducts: (validIds) =>
				set((s) => {
					const keep = new Set(validIds);
					const next = s.ids.filter((id) => keep.has(id));
					if (next.length === s.ids.length) return s;
					const nextSizes = Object.fromEntries(Object.entries(s.sizes).filter(([id]) => keep.has(id)));
					return { ids: next, sizes: nextSizes };
				}),
			syncFromServer: async () => {
				const writesAtStart = likeWrites;
				const state = hasBuyerToken()
					? await api.buyer.importGuestLikes(get().clientId)
					: await api.engagement.getLikes(get().clientId);
				// Пока шёл запрос, покупатель ставил/снимал лайк — ответ устарел,
				// локальный список новее. Следующая сверка всё выровняет.
				if (likeWrites !== writesAtStart || likeWritesInFlight > 0) return state;
				// Сервер — источник истины (каждый лайк пишется на сервер), поэтому
				// заменяем, а не аккумулируем: иначе снятые/чужие id копятся и бейдж
				// расходится с реальным избранным.
				get().replaceServerState(state);
				return state;
			},
			setProductLikedOnServer: (productId, liked, size) =>
				writeLike(
					"ids",
					productId,
					liked,
					() =>
						hasBuyerToken()
							? api.buyer.setProductLiked(productId, liked, size)
							: api.engagement.setProductLiked(get().clientId, productId, liked, size),
					(s) => ({ ids: liked ? s.ids.filter((x) => x !== productId) : uniq([...s.ids, productId]) }),
					set,
					get,
				),
			setShopLikedOnServer: (shopId, liked) =>
				writeLike(
					"shopIds",
					shopId,
					liked,
					() =>
						hasBuyerToken()
							? api.buyer.setShopLiked(shopId, liked)
							: api.engagement.setShopLiked(get().clientId, shopId, liked),
					(s) => ({ shopIds: liked ? s.shopIds.filter((x) => x !== shopId) : uniq([...s.shopIds, shopId]) }),
					set,
					get,
				),
			setIdeaLikedOnServer: (ideaId, liked) =>
				writeLike(
					"ideaIds",
					ideaId,
					liked,
					() =>
						hasBuyerToken()
							? api.buyer.setIdeaLiked(ideaId, liked)
							: api.engagement.setIdeaLiked(get().clientId, ideaId, liked),
					(s) => ({ ideaIds: liked ? s.ideaIds.filter((x) => x !== ideaId) : uniq([...s.ideaIds, ideaId]) }),
					set,
					get,
				),
			clear: () => set({ ids: [], shopIds: [], ideaIds: [], sizes: {} }),
			resetDevice: () =>
				set({ clientId: newClientId(), lastSeenAt: Date.now(), ids: [], shopIds: [], ideaIds: [], sizes: {} }),
			touch: () => set({ lastSeenAt: Date.now() }),
			applyServerStateSince: (mark, state) => {
				if (likeWrites !== mark || likeWritesInFlight > 0) return;
				get().replaceServerState(state);
			},
		}),
		{
			name: "osmix-wishlist",
			storage: createJSONStorage(() => safeLocalStorage),
			partialize: (s) => ({ clientId: s.clientId, lastSeenAt: s.lastSeenAt, ids: s.ids, shopIds: s.shopIds, ideaIds: s.ideaIds, sizes: s.sizes }),
			onRehydrateStorage: () => (state) => {
				if (state && !state.clientId) state.clientId = newClientId();
			},
		},
	),
);
